import React, { useState, useEffect } from 'react';
import {
    Container,
    Paper,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Button,
    Box,
    CircularProgress,
    Alert,
    Grid,
    Card,
    CardContent,
    TablePagination,
    IconButton,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions
} from '@mui/material';
import { 
    Sync as SyncIcon, 
    Refresh as RefreshIcon,
    Search as SearchIcon,
    Delete as DeleteIcon
} from '@mui/icons-material';
import apiClient from '../services/apiClient';
import ExemptionSearchModal from '../components/ExemptionSearchModal';

interface Exemption {
    id: string;
    recup_id: string;
    code: string;
    name: string;
    description: string;
    created_at: string;
    updated_at: string;
}

const ExemptionsPage: React.FC = () => {
    console.log('🔓 ExemptionsPage: Component mounted/rendered');
    
    const [exemptions, setExemptions] = useState<Exemption[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [syncing, setSyncing] = useState(false);
    
    // Pagination
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [totalCount, setTotalCount] = useState(0);

    // Nuove funzionalità
    const [searchModalOpen, setSearchModalOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [exemptionToDelete, setExemptionToDelete] = useState<Exemption | null>(null);
    const [deleting, setDeleting] = useState(false);

    const fetchExemptions = async () => {
        console.log('🔓 ExemptionsPage: fetchExemptions called');
        setLoading(true);
        setError(null);
        try {
            const params = new URLSearchParams({
                limit: rowsPerPage.toString(),
                offset: (page * rowsPerPage).toString()
            });

            console.log('🔓 ExemptionsPage: Making API call to:', `/recup-sync/exemptions?${params}`);
            const response = await apiClient.get(`/recup-sync/exemptions?${params}`);
            console.log('🔓 ExemptionsPage: API response received:', response.data);
            
            if (response.data.success) {
                console.log('🔓 ExemptionsPage: Raw exemptions data:', response.data.data);
                console.log('🔓 ExemptionsPage: First exemption object:', response.data.data[0]);
                setExemptions(response.data.data);
                setTotalCount(response.data.pagination?.total || response.data.data.length);
                console.log('🔓 ExemptionsPage: Exemptions data set successfully');
            } else {
                setError(response.data.error || 'Errore nel recupero delle esenzioni');
                console.error('🔓 ExemptionsPage: API returned error:', response.data.error);
            }
        } catch (err: any) {
            console.error('🔓 ExemptionsPage: API call failed:', err);
            setError(err.response?.data?.error || err.message || 'Errore nel recupero delle esenzioni');
        } finally {
            setLoading(false);
        }
    };

    const handleSync = async () => {
        setSyncing(true);
        setError(null);
        try {
            const response = await apiClient.post('/recup-sync/sync/exemptions');
            if (response.data.success) {
                await fetchExemptions();
                alert(`Sincronizzazione completata con successo!\nCreati: ${response.data.created}\nAggiornati: ${response.data.updated}`);
            } else {
                setError(response.data.error || 'Errore nella sincronizzazione');
            }
        } catch (err: any) {
            setError(err.response?.data?.error || err.message || 'Errore nella sincronizzazione');
        } finally {
            setSyncing(false);
        }
    };

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleDeleteClick = (exemption: Exemption) => {
        console.log('🔓 ExemptionsPage: Delete clicked for exemption:', exemption);
        console.log('🔓 ExemptionsPage: exemption.recup_id:', exemption.recup_id);
        setExemptionToDelete(exemption);
        setDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = async () => {
        if (!exemptionToDelete) return;

        setDeleting(true);
        setError(null);

        try {
            const response = await apiClient.delete(`/recup-sync/exemptions/${exemptionToDelete.recup_id}`);
            
            if (response.data.success) {
                await fetchExemptions();
                setDeleteDialogOpen(false);
                setExemptionToDelete(null);
                alert(`Esenzione ${exemptionToDelete.code} eliminata con successo`);
            } else {
                setError(response.data.error || 'Errore durante l\'eliminazione');
            }
        } catch (err: any) {
            console.error('Delete error:', err);
            setError(err.response?.data?.error || 'Errore durante l\'eliminazione');
        } finally {
            setDeleting(false);
        }
    };

    const handleDeleteCancel = () => {
        setDeleteDialogOpen(false);
        setExemptionToDelete(null);
    };

    const handleSearchSync = () => {
        setSearchModalOpen(true);
    };

    const handleSyncComplete = () => {
        fetchExemptions();
    };

    useEffect(() => {
        console.log('🔓 ExemptionsPage: useEffect for exemptions triggered with:', { page, rowsPerPage });
        fetchExemptions();
    }, [page, rowsPerPage]);

    console.log('🔓 ExemptionsPage: Rendering component');
    
    return (
        <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
            <Typography variant="h4" gutterBottom>
                Gestione Esenzioni
            </Typography>

            {/* Statistiche */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={4}>
                    <Card>
                        <CardContent>
                            <Typography color="textSecondary" gutterBottom>
                                Totale Esenzioni
                            </Typography>
                            <Typography variant="h5">
                                {totalCount}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                    <Card>
                        <CardContent>
                            <Typography color="textSecondary" gutterBottom>
                                Esenzioni con Codice
                            </Typography>
                            <Typography variant="h5">
                                {exemptions.filter(e => e.code).length}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                    <Card>
                        <CardContent>
                            <Typography color="textSecondary" gutterBottom>
                                Esenzioni con Descrizione
                            </Typography>
                            <Typography variant="h5">
                                {exemptions.filter(e => e.description).length}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Controlli */}
            <Paper sx={{ p: 2, mb: 3 }}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                    <Button
                        variant="outlined"
                        onClick={fetchExemptions}
                        disabled={loading}
                        startIcon={<RefreshIcon />}
                    >
                        Aggiorna
                    </Button>
                    
                    <Button
                        variant="contained"
                        onClick={handleSearchSync}
                        disabled={syncing || loading}
                        startIcon={<SearchIcon />}
                        color="primary"
                    >
                        Cerca e Sincronizza da RECUP
                    </Button>

                    <Button
                        variant="outlined"
                        onClick={handleSync}
                        disabled={syncing}
                        startIcon={syncing ? <CircularProgress size={20} /> : <SyncIcon />}
                        color="secondary"
                    >
                        {syncing ? 'Sincronizzazione...' : 'Sincronizza Tutte'}
                    </Button>
                </Box>
            </Paper>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            {/* Tabella Esenzioni */}
            <Paper sx={{ width: '100%', overflow: 'hidden' }}>
                <TableContainer>
                    <Table stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell>ID</TableCell>
                                <TableCell>Codice</TableCell>
                                <TableCell>Nome</TableCell>
                                <TableCell>Descrizione</TableCell>
                                <TableCell>Creato</TableCell>
                                <TableCell>Aggiornato</TableCell>
                                <TableCell align="center">Azioni</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell style={{ textAlign: 'center' }} colSpan={7}>
                                        <CircularProgress />
                                    </TableCell>
                                </TableRow>
                            ) : exemptions.length === 0 ? (
                                <TableRow>
                                    <TableCell style={{ textAlign: 'center' }} colSpan={7}>
                                        Nessuna esenzione trovata
                                    </TableCell>
                                </TableRow>
                            ) : (
                                exemptions.map((exemption) => (
                                    <TableRow key={exemption.id}>
                                        <TableCell>{exemption.id}</TableCell>
                                        <TableCell>
                                            <Typography variant="body2" fontWeight="bold">
                                                {exemption.code}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>{exemption.name}</TableCell>
                                        <TableCell>
                                            {exemption.description && (
                                                <Typography 
                                                    variant="body2" 
                                                    sx={{ 
                                                        maxWidth: 300, 
                                                        overflow: 'hidden', 
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap'
                                                    }}
                                                    title={exemption.description}
                                                >
                                                    {exemption.description}
                                                </Typography>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {new Date(exemption.created_at).toLocaleDateString()}
                                        </TableCell>
                                        <TableCell>
                                            {new Date(exemption.updated_at).toLocaleDateString()}
                                        </TableCell>
                                        <TableCell align="center">
                                            <Tooltip title="Elimina esenzione dal database locale">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleDeleteClick(exemption)}
                                                    disabled={deleting}
                                                    color="error"
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    component="div"
                    count={totalCount}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    labelRowsPerPage="Righe per pagina:"
                    labelDisplayedRows={({ from, to, count }) =>
                        `${from}-${to} di ${count !== -1 ? count : `più di ${to}`}`
                    }
                />
            </Paper>

            {/* Modale di ricerca */}
            <ExemptionSearchModal
                open={searchModalOpen}
                onClose={() => setSearchModalOpen(false)}
                onSyncComplete={handleSyncComplete}
            />

            {/* Dialog di conferma eliminazione */}
            <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
                <DialogTitle>
                    Conferma Eliminazione
                </DialogTitle>
                <DialogContent>
                    <Typography>
                        Sei sicuro di voler eliminare l'esenzione <strong>{exemptionToDelete?.code}</strong> dal database locale?
                    </Typography>
                    <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                        Nota: L'esenzione verrà eliminata solo dal database locale, non dal sistema RECUP.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button 
                        onClick={handleDeleteCancel} 
                        disabled={deleting}
                    >
                        Annulla
                    </Button>
                    <Button 
                        onClick={handleDeleteConfirm} 
                        disabled={deleting}
                        color="error"
                        variant="contained"
                        startIcon={deleting ? <CircularProgress size={20} /> : <DeleteIcon />}
                    >
                        {deleting ? 'Eliminazione...' : 'Elimina'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default ExemptionsPage;